/**
 * `<Select>` keyboard intent handler — extracted from `Select.tsx` so
 * the component file stays under the 500-line functional cap. Pure
 * function: reads the supplied deps, calls back into the component's
 * highlight / commit / open / close APIs via the `actions` bag.
 */

import type { KeyboardEvent as ReactKeyboardEvent } from 'react';

import type { SelectOption } from './types.js';
import { findEnabledIndex } from './utils.js';

export interface SelectKeyDeps<V extends string> {
  readonly disabled: boolean;
  readonly open: boolean;
  readonly searchable: boolean;
  readonly options: readonly SelectOption<V>[];
  readonly highlightIndex: number;
  readonly isVisible: (i: number) => boolean;
}

export interface SelectKeyActions<V extends string> {
  readonly openPanel: (focus: 'first' | 'last' | 'selected') => void;
  readonly closePanel: (returnFocus: boolean) => void;
  readonly updateHighlight: (i: number) => void;
  readonly commit: (opt: SelectOption<V>) => void;
  readonly typeaheadMatch: (char: string) => void;
}

/**
 * Translate a key event on the `<Select>` trigger or inside the open
 * panel into the appropriate action call. Returns `true` if the event
 * was handled (caller should not bubble further processing).
 */
export function handleSelectKey<V extends string>(
  e: ReactKeyboardEvent<HTMLElement>,
  deps: SelectKeyDeps<V>,
  actions: SelectKeyActions<V>,
): void {
  if (deps.disabled) return;
  const { key } = e;
  const { open, options, highlightIndex, isVisible, searchable } = deps;
  if (key === 'ArrowDown' || key === 'Down') {
    e.preventDefault();
    if (!open) actions.openPanel('first');
    else actions.updateHighlight(findEnabledIndex(options, highlightIndex, 1, isVisible));
    return;
  }
  if (key === 'ArrowUp' || key === 'Up') {
    e.preventDefault();
    if (!open) actions.openPanel('last');
    else actions.updateHighlight(findEnabledIndex(options, highlightIndex, -1, isVisible));
    return;
  }
  if (!open && (key === 'Enter' || key === ' ' || key === 'Spacebar')) {
    e.preventDefault();
    actions.openPanel('selected');
    return;
  }
  if (open && key === 'Enter') {
    e.preventDefault();
    const opt = highlightIndex >= 0 ? options[highlightIndex] : undefined;
    if (opt !== undefined) actions.commit(opt);
    actions.closePanel(true);
    return;
  }
  if (open && !searchable && (key === ' ' || key === 'Spacebar')) {
    e.preventDefault();
    const opt = highlightIndex >= 0 ? options[highlightIndex] : undefined;
    if (opt !== undefined) actions.commit(opt);
    actions.closePanel(true);
    return;
  }
  if (key === 'Escape' && open) {
    e.preventDefault();
    actions.closePanel(true);
    return;
  }
  if (key === 'Home' && open) {
    e.preventDefault();
    actions.updateHighlight(findEnabledIndex(options, -1, 1, isVisible));
    return;
  }
  if (key === 'End' && open) {
    e.preventDefault();
    actions.updateHighlight(findEnabledIndex(options, 0, -1, isVisible));
    return;
  }
  if (key === 'Tab' && open) {
    const opt = highlightIndex >= 0 ? options[highlightIndex] : undefined;
    if (opt !== undefined) actions.commit(opt);
    actions.closePanel(false);
    return;
  }
  /* Typeahead on the trigger only (when the search box has its own input flow). */
  if (!open && key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
    actions.typeaheadMatch(key);
  }
}
